import { getPlaylistData } from 'services/boltYoutube';
import { arrayWithUniqueKeys } from 'utils/bolt';
import slugify from 'utils/slugify';
import { accessNested, commaSeparatedList, sortObjectByOrder } from 'utils/utils';
import cached from './cached';

/**
 * Loads the video list from the store. If any video in the list is a YouTube playlist, it fetches the videos
 * from that playlist and returns the full list of videos.
 * @param {Object} store The application store.
 * @param {string} pageId The page ID where the video list is located in the store (default is `boltVideos`).
 * @returns {Promise<Array>} A promise that resolves to an array of video objects.
 */
export async function loadVideoList(store, pageId = 'boltVideos') {
	const videoDataList = getVideoAppData(store, pageId);
	return await replacePlaylistWithVideos(videoDataList);
}

/**
 * Loads the first video from the video list in the store. If the first video is a playlist, it fetches the videos 
 * from that playlist and returns the first video from the playlist.
 * @param {Object} store The application store.
 * @param {string} pageId The page ID where the video list is located in the store (default is `boltVideos`).
 * @returns {Promise<Object|undefined>} A promise that resolves to the first video object or undefined if not found.
 */
export async function loadFirstVideoFromList(store, pageId = 'boltVideos') {
	const videoDataList = getVideoAppData(store, pageId);
	const firstVideo = videoDataList[0];

	if (!firstVideo?.youtubePlaylistId) return firstVideo;

	const playlistVideos = await getVideosFromPlaylist(firstVideo);
	return playlistVideos[0];
}

function getVideoAppData(state, pageId) {
	const videoAppData = accessNested(state, `appData.pages.${pageId}.data.list`, {});
	return sortObjectByOrder(videoAppData, (value, key) => ({ ...value, key: slugify(key) }));
}

export async function replacePlaylistWithVideos(videos) {
	const promises = videos.map(vid => vid?.youtubePlaylistId ? getVideosFromPlaylist(vid) : vid);
	const data = await Promise.all(promises);
	const concatArray = [].concat(...data);
	return arrayWithUniqueKeys(concatArray);
}

const getVideosFromPlaylist = cached(loadVideosFromPlaylist, playlist => playlist.youtubePlaylistId);
async function loadVideosFromPlaylist(playlist) {
	const playlistId = playlist.youtubePlaylistId;
	const noCookies = playlist.noCookies;

	try {
		const data = await getPlaylistData(playlistId);
		const mappedItems = data.items
			.filter(item => item.snippet.thumbnails.medium)
			.map(item => ({
				name: item.snippet.title,
				thumbSrc: item.snippet.thumbnails.medium.url,
				youtubeId: item.snippet.resourceId.videoId,
				key: item.id,
				playlistId, // Keep track of which playlist this video came from
				noCookies
			}));

		// Apply sorting, if specified
		if (playlist.playlistOrder) {
			// Get the playlist order and reverse it for correct sorting (last in list appears first)
			const playlistOrder = commaSeparatedList(playlist.playlistOrder).reverse();
			// Sort the mapped items based on the playlist order, if not found, keep original order
			return mappedItems.sort((a, b) => playlistOrder.indexOf(a.youtubeId) - playlistOrder.indexOf(b.youtubeId));
		}

		return mappedItems;
	} catch {
		return [];
	}
}
